// src/utils/formListsDependencyCleanupUtils.js

/**
 * formListsDependencyCleanupUtils
 * -----------------------------------------------------------------------------
 * Pure utilities for cleaning dropdown dependency graphs against the dropdown
 * options that are actually being saved/published.
 *
 * WHY THIS FILE EXISTS
 *  • Spreadsheet cell clearing must stay local and fast.
 *  • Dependency cleanup must NOT happen inside the shared Spreadsheet/ engine.
 *  • Dependency cleanup must NOT depend on React effect timing.
 *  • Save Draft / Publish need a deterministic cleanup snapshot before
 *    compression.
 *
 * ENTERPRISE RULE
 *  • Cleanup is performed at intentional persistence boundaries:
 *      - Save Draft
 *      - Publish
 *  • Cleanup is NOT performed during spreadsheet keydown.
 *  • Cleanup is NOT performed by the shared spreadsheet engine.
 *
 * SCHEMA PRESERVED
 *  dropdownDependencies shape remains:
 *
 *  {
 *    [optionsSetUFI]: {
 *      [optionId]: {
 *        dependentOptionIds: [...]
 *      }
 *    }
 *  }
 */

const isPlainObject = (value) => (
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value)
);

const normalizeId = (value) => (
  typeof value === 'string' ? value.trim() : ''
);

const toOptionArray = (value) => (
  Array.isArray(value) ? value : []
);

const buildOptionIdIndexes = (dropdownOptions = {}) => {
  const validOptionIdsBySet = {};
  const allValidOptionIds = new Set();

  Object.entries(dropdownOptions || {}).forEach(([optionsSetUFI, options]) => {
    const safeOptionsSetUFI = normalizeId(optionsSetUFI);
    if (!safeOptionsSetUFI) return;

    const optionIdsForSet = new Set();

    toOptionArray(options).forEach((option) => {
      const optionId = normalizeId(option?.optionId);
      if (!optionId) return;

      optionIdsForSet.add(optionId);
      allValidOptionIds.add(optionId);
    });

    validOptionIdsBySet[safeOptionsSetUFI] = optionIdsForSet;
  });

  return {
    validOptionIdsBySet,
    allValidOptionIds,
  };
};

/**
 * cleanDropdownDependenciesForOptions
 * -----------------------------------------------------------------------------
 * Cleans dependency rules against the dropdownOptions snapshot being saved or
 * published.
 *
 * WHAT IT REMOVES
 *  • dependency sets for option sets that no longer exist
 *  • trigger option dependency entries where the trigger option no longer
 *    exists inside its own option set
 *  • dependentOptionIds that no longer exist in any active dropdown option set
 *
 * WHAT IT PRESERVES
 *  • dependency entries for valid trigger options
 *  • empty dependency entries for valid trigger options by default, matching
 *    the current DropdownDependenciesContext behavior
 *  • all extra fields on dependency entry objects, if any are added later
 *
 * IMPORTANT
 *  • This function is pure.
 *  • It does not mutate dropdownOptions.
 *  • It does not mutate dropdownDependencies.
 *  • It does not read or write storage.
 *  • It does not depend on React.
 */
export const cleanDropdownDependenciesForOptions = ({
  dropdownOptions = {},
  dropdownDependencies = {},
  preserveEmptyDependencyEntries = true,
} = {}) => {
  const {
    validOptionIdsBySet,
    allValidOptionIds,
  } = buildOptionIdIndexes(dropdownOptions);

  const cleanedDropdownDependencies = {};

  const diagnostics = {
    activeOptionSetCount: Object.keys(validOptionIdsBySet).length,
    originalOptionSetCount: Object.keys(dropdownDependencies || {}).length,
    cleanedOptionSetCount: 0,
    removedOptionSetUFIs: [],
    removedTriggerOptionIds: [],
    removedDependentOptionIds: [],
    preservedTriggerOptionIds: [],
  };

  /**
   * Preserve an empty object for every active option set.
   *
   * WHY
   *  • Your current dependency shape often includes empty option-set objects.
   *  • Keeping that shape avoids unnecessary downstream assumptions changing.
   */
  Object.keys(validOptionIdsBySet).forEach((optionsSetUFI) => {
    cleanedDropdownDependencies[optionsSetUFI] = {};
  });

  Object.entries(dropdownDependencies || {}).forEach(([
    optionsSetUFI,
    dependenciesForSet,
  ]) => {
    const safeOptionsSetUFI = normalizeId(optionsSetUFI);
    if (!safeOptionsSetUFI) return;

    const validTriggerOptionIds = validOptionIdsBySet[safeOptionsSetUFI];

    if (!validTriggerOptionIds) {
      diagnostics.removedOptionSetUFIs.push(safeOptionsSetUFI);
      return;
    }

    const sourceDependenciesForSet = isPlainObject(dependenciesForSet)
      ? dependenciesForSet
      : {};

    Object.entries(sourceDependenciesForSet).forEach(([
      optionId,
      dependencyEntry,
    ]) => {
      const safeOptionId = normalizeId(optionId);
      if (!safeOptionId) return;

      if (!validTriggerOptionIds.has(safeOptionId)) {
        diagnostics.removedTriggerOptionIds.push({
          optionsSetUFI: safeOptionsSetUFI,
          optionId: safeOptionId,
        });
        return;
      }

      const rawDependentOptionIds = Array.isArray(
        dependencyEntry?.dependentOptionIds
      )
        ? dependencyEntry.dependentOptionIds
        : [];

      const filteredDependentOptionIds = rawDependentOptionIds.filter((depId) => {
        const safeDependentOptionId = normalizeId(depId);
        const keep = safeDependentOptionId && allValidOptionIds.has(safeDependentOptionId);

        if (!keep && safeDependentOptionId) {
          diagnostics.removedDependentOptionIds.push({
            optionsSetUFI: safeOptionsSetUFI,
            optionId: safeOptionId,
            dependentOptionId: safeDependentOptionId,
          });
        }

        return keep;
      });

      if (
        filteredDependentOptionIds.length > 0 ||
        preserveEmptyDependencyEntries
      ) {
        cleanedDropdownDependencies[safeOptionsSetUFI][safeOptionId] = {
          ...(isPlainObject(dependencyEntry) ? dependencyEntry : {}),
          dependentOptionIds: filteredDependentOptionIds,
        };

        diagnostics.preservedTriggerOptionIds.push({
          optionsSetUFI: safeOptionsSetUFI,
          optionId: safeOptionId,
          dependentCount: filteredDependentOptionIds.length,
        });
      }
    });
  });

  diagnostics.cleanedOptionSetCount =
    Object.keys(cleanedDropdownDependencies).length;

  return {
    dropdownDependencies: cleanedDropdownDependencies,
    diagnostics,
  };
};

export default cleanDropdownDependenciesForOptions;